// User Management API Service - Backend Integration (Organizational)
import { apiGet, apiPost, apiPut, apiDelete } from '../services/apiClient';
import MockDataService from '../services/MockDataService';

const USE_MOCK = process.env.REACT_APP_USE_MOCK_DATA === 'true' || process.env.NODE_ENV === 'development';

// Helper to wrap response in { data: ... } format for backward compatibility with axios-style responses
const wrapResponse = (data) => ({ data });

/**
 * Get users with pagination
 * Gateway path: /gateway/registration/api/signup/organization/users
 */
export const getUsers = async (params = {}) => {
  const response = await apiGet('/gateway/registration/api/signup/organization/users', params);
  return wrapResponse(response);
};

/**
 * Get agents (users with AGENT role) with pagination
 * Gateway path: /gateway/registration/api/signup/organization/agents
 */
export const getAgents = async (params = {}) => {
  const response = await apiGet('/gateway/registration/api/signup/organization/agents', params);
  return wrapResponse(response);
};

/**
 * Get user by ID
 * Gateway path: /gateway/registration/api/signup/organization/users/{userId}
 */
export const getUserById = async (userId) => {
  if (USE_MOCK) {
    try {
      const response = await MockDataService.getUserById(userId);
      return wrapResponse(response.data);
    } catch (error) {
      const response = await apiGet(`/gateway/registration/api/signup/organization/users/${userId}`);
      return wrapResponse(response);
    }
  }
  try {
    const response = await apiGet(`/gateway/registration/api/signup/organization/users/${userId}`);
    return wrapResponse(response);
  } catch (error) {
    const response = await MockDataService.getUserById(userId);
    return wrapResponse(response.data);
  }
};

/**
 * Update user
 * Gateway path: /gateway/registration/api/signup/organization/users/{userId}
 */
export const updateUser = async (userId, data) => {
  const response = await apiPut(`/gateway/registration/api/signup/organization/users/${userId}`, data);
  return wrapResponse(response);
};

/**
 * Delete user
 * Gateway path: /gateway/registration/api/signup/organization/users/{userId}
 */
export const deleteUser = async (userId) => {
  const response = await apiDelete(`/gateway/registration/api/signup/organization/users/${userId}`);
  return wrapResponse(response);
};

/**
 * Get user roles
 * Gateway path: /gateway/registration/api/signup/organization/users/{userId}/roles
 */
export const getUserRoles = async (userId) => {
  if (USE_MOCK) {
    try {
      const response = await MockDataService.getUserRoles(userId);
      return wrapResponse(response.data);
    } catch (error) {
      const response = await apiGet(`/gateway/registration/api/signup/organization/users/${userId}/roles`);
      return wrapResponse(response);
    }
  }
  try {
    const response = await apiGet(`/gateway/registration/api/signup/organization/users/${userId}/roles`);
    return wrapResponse(response);
  } catch (error) {
    const response = await MockDataService.getUserRoles(userId);
    return wrapResponse(response.data);
  }
};

/**
 * Get user teams
 * Gateway path: /gateway/registration/api/signup/organization/users/{userId}/teams
 */
export const getUserTeams = async (userId) => {
  if (USE_MOCK) {
    try {
      const response = await MockDataService.getUserTeams(userId);
      return wrapResponse(response.data);
    } catch (error) {
      const response = await apiGet(`/gateway/registration/api/signup/organization/users/${userId}/teams`);
      return wrapResponse(response);
    }
  }
  try {
    const response = await apiGet(`/gateway/registration/api/signup/organization/users/${userId}/teams`);
    return wrapResponse(response);
  } catch (error) {
    const response = await MockDataService.getUserTeams(userId);
    return wrapResponse(response.data);
  }
};

/**
 * Assign department to user
 * Gateway path: /gateway/registration/api/signup/organization/users/{userId}/department
 */
export const assignDepartment = async (userId, departmentId) => {
  const response = await apiPut(`/gateway/registration/api/signup/organization/users/${userId}/department?departmentId=${departmentId}`, null);
  return wrapResponse(response);
};

/**
 * Assign manager to user
 * Gateway path: /gateway/registration/api/signup/organization/users/{userId}/manager
 */
export const assignManager = async (userId, managerId) => {
  const response = await apiPut(`/gateway/registration/api/signup/organization/users/${userId}/manager?managerId=${managerId}`, null);
  return wrapResponse(response);
};

/**
 * Activate user
 * Gateway path: /gateway/registration/api/signup/organization/users/{userId}/activate
 */
export const activateUser = async (userId) => {
  const response = await apiPost(`/gateway/registration/api/signup/organization/users/${userId}/activate`, null);
  return wrapResponse(response);
};

/**
 * Deactivate user
 * Gateway path: /gateway/registration/api/signup/organization/users/{userId}/deactivate
 */
export const deactivateUser = async (userId) => {
  const response = await apiPost(`/gateway/registration/api/signup/organization/users/${userId}/deactivate`, null);
  return wrapResponse(response);
};

/**
 * Bulk create users (JSON body)
 * POST /gateway/registration/api/signup/organization/users/bulk
 */
export const bulkCreateUsers = async (users) => {
  const response = await apiPost('/gateway/registration/api/signup/organization/users/bulk', {
    users,
  });
  return wrapResponse(response);
};

/**
 * Bulk import users (same backend logic as bulk create)
 * POST /gateway/registration/api/signup/organization/import/users
 */
export const bulkImportUsers = async (users) => {
  const response = await apiPost('/gateway/registration/api/signup/organization/import/users', {
    users,
  });
  return wrapResponse(response);
};

// Service object for backward compatibility
export const userManagementService = {
  getUsers,
  getAgents,
  getUserById,
  updateUser,
  deleteUser,
  getUserRoles,
  getUserTeams,
  assignDepartment,
  assignManager,
  activateUser,
  deactivateUser,
  bulkCreateUsers,
  bulkImportUsers,
};

export default userManagementService;
